import { sortParam } from '../sort.js';
import { z } from 'zod';
import { paginationQuery } from '../api.js';
import { nullableText } from '../common.js';
import { TAX_RATES } from '../offers/calc.js';
/**
 * Verträge des Moduls Produkte (KON-107). Beträge sind ganze Cent, das Gewicht Gramm, die Maße Millimeter. Die Oberfläche zeigt Euro, kg und cm.
 */
export const PRODUCT_KINDS = ['goods', 'service'];
export const PRODUCT_KIND_LABELS = { goods: 'Ware', service: 'Dienstleistung' };
/** Höchstens so viele Bilder je Produkt, höchstens so groß je Bild (KON-109) */
export const PRODUCT_IMAGE_LIMITS = { maxCount: 10, maxBytes: 2 * 1024 * 1024, maxEdge: 10000, types: ['image/webp', 'image/png', 'image/jpeg'] };
/**
 * Prüfziffer einer EAN/GTIN (8, 12, 13 oder 14 Stellen): Von rechts gezählt werden die Ziffern abwechselnd mit 3 und 1 gewichtet, die Summe muss mit der
 * Prüfziffer ein Vielfaches von 10 ergeben.
 */
export function isValidGtin(value) {
    if (!/^(\d{8}|\d{12,14})$/.test(value))
        return false;
    const digits = [...value].map(Number);
    const check = digits.pop();
    const sum = digits.reverse().reduce((s, d, i) => s + d * (i % 2 === 0 ? 3 : 1), 0);
    return (10 - (sum % 10)) % 10 === check;
}
const taxRate = z.union(TAX_RATES.map((r) => z.literal(r)), 'Steuersatz 19, 7 oder 0 %');
const cents = z.number().int('Betrag in Cent').min(0, 'Der Preis darf nicht negativ sein').max(1_000_000_000, 'Betrag zu groß');
const size = (label) => z.number().int(`${label} als ganze Zahl`).min(0, `${label} darf nicht negativ sein`).max(100_000_000, `${label} zu groß`);
const id = (message) => z.uuid(message).nullable().optional();
/**
 * Produktbuchungsgruppe: Sie legt fest, auf welches Sachkonto der Erlös eines Produkts gebucht wird, wenn es auf einer Rechnung steht. Der Steuerberater wählt das Konto. Eine Gruppe der Art
 * „Gutschein“ gehört zu Gutschein-Artikeln: Deren Verkauf ist kein Erlös, das Konto ist das der Verbindlichkeit gegenüber dem Inhaber.
 */
export const BOOKING_GROUP_KINDS = ['revenue', 'gift_voucher'];
export const BOOKING_GROUP_KIND_LABELS = { revenue: 'Erlös', gift_voucher: 'Gutschein (Verbindlichkeit)' };
const accountNumber = z
    .string()
    .trim()
    .regex(/^[0-9]{1,8}$/, 'Nur Ziffern, höchstens 8 Stellen')
    .nullable()
    .or(z.literal('').transform(() => null));
const groupName = z.string().trim().min(1, 'Name fehlt').max(100, 'Höchstens 100 Zeichen');
export const bookingGroupInput = z
    .object({
    name: groupName,
    kind: z.enum(BOOKING_GROUP_KINDS, 'Bitte die Art wählen').default('revenue'),
    accountNumber: accountNumber.optional().transform((v) => v ?? null),
    note: nullableText(500),
})
    .strict();
/** Ändern: nicht genannte Felder bleiben. Eine leere Eingabe beim Konto löscht es. */
export const updateBookingGroupInput = z
    .object({ name: groupName, kind: z.enum(BOOKING_GROUP_KINDS, 'Bitte die Art wählen'), accountNumber, note: nullableText(500) })
    .partial()
    .strict();
const fields = {
    name: z.string().trim().min(2, 'Bezeichnung fehlt').max(200, 'Höchstens 200 Zeichen'),
    sku: nullableText(60),
    kind: z.enum(PRODUCT_KINDS, 'Bitte Ware oder Dienstleistung wählen'),
    categoryId: id('Ungültige Kategorie'),
    unitId: id('Ungültige Einheit'),
    /** Buchungsgruppe: bestimmt das Sachkonto beim Buchen. Leer: Das Konto richtet sich nach dem Steuerschlüssel der Rechnung. */
    bookingGroupId: id('Ungültige Buchungsgruppe'),
    description: nullableText(5000),
    salePriceCents: cents,
    taxRate,
    purchasePriceCents: cents.nullable().optional(),
    supplierId: id('Ungültiger Lieferant'),
    supplierSku: nullableText(60),
    ean: z
        .string()
        .trim()
        .nullable()
        .optional()
        .transform((v) => (v ? v.replace(/\s+/g, '') : v === '' ? null : v))
        .refine((v) => v === null || v === undefined || isValidGtin(v), 'Ungültige EAN/GTIN. Sie hat 8, 12, 13 oder 14 Ziffern, die letzte ist eine Prüfziffer.'),
    manufacturer: nullableText(120),
    manufacturerSku: nullableText(60),
    weightGrams: size('Gewicht').nullable().optional(),
    lengthMm: size('Länge').nullable().optional(),
    widthMm: size('Breite').nullable().optional(),
    heightMm: size('Höhe').nullable().optional(),
    note: nullableText(5000),
    isActive: z.boolean(),
    /** Aus: wird nicht verkauft (Verbrauchsmaterial), erscheint nicht in Angeboten, Aufträgen und Rechnungen */
    isSellable: z.boolean(),
    /** Ersetzt beim Ändern alle Tags */
    tagIds: z.array(z.uuid('Ungültiger Tag')).max(30, 'Höchstens 30 Tags').transform((ids) => [...new Set(ids)]),
};
export const createProductInput = z
    .object({ ...fields, kind: fields.kind.default('goods'), salePriceCents: cents.default(0), taxRate: taxRate.default(19), isActive: fields.isActive.default(true), isSellable: fields.isSellable.default(true), tagIds: fields.tagIds.default([]) })
    .strict();
export const updateProductInput = z
    .object(fields)
    .partial()
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const listProductsQuery = paginationQuery.extend({
    q: z.string().trim().max(100).optional(),
    /** Eine Hauptkategorie schließt ihre Unterkategorien ein */
    categoryId: z.uuid().optional(),
    kind: z.enum(PRODUCT_KINDS).optional(),
    tagId: z.uuid().optional(),
    status: z.enum(['active', 'inactive', 'all']).default('active'),
    /** Nur verkaufte oder nur nicht verkaufte Produkte (Verbrauchsmaterial) */
    sellable: z.enum(['yes', 'no']).optional(),
    sort: sortParam(['name', 'sku', 'kind', 'price', 'createdAt'], 'name'),
});
/** Neue Reihenfolge der Bilder, vollständig. Das erste wird Hauptbild. */
export const reorderProductImagesInput = z.object({ ids: z.array(z.uuid('Ungültiges Bild')).min(1).max(PRODUCT_IMAGE_LIMITS.maxCount) });
/** Auswahl im Angebot (KON-113): nur aktive Produkte, nur was die Position braucht */
export const productPickerQuery = z.object({
    q: z.string().trim().max(100).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    /** „sale“ (Vorgabe): nur verkaufte Produkte, für Angebote, Aufträge und Rechnungen. „stock“: alle Waren, für das Lager. */
    purpose: z.enum(['sale', 'stock']).default('sale'),
});
//# sourceMappingURL=products.js.map